import { formatDistanceToNow } from 'date-fns';
import type { ValorantRegion, ValorantServer } from 'generated/openapi';
import { Clock, SignalHigh } from 'lucide-react';
import { type HTMLAttributes, memo } from 'react';

import CrossedSwords from '@/assets/icons/crossed-swords.svg?react';
import Valorant from '@/assets/icons/valorant.svg?react';
import { getRegionDisplayName } from '@/features/games/valorant/utils/region';
import { SERVER_DISPLAY_NAMES } from '@/features/games/valorant/utils/servers';
import { Card } from '@/shared/components/ui/card';
import { cn } from '@/shared/utils/cn';

const InfoRow = ({
  icon,
  children,
  className,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={cn('flex items-center gap-2', className)}>
    <div className="text-cold-300 shrink-0">{icon}</div>
    <div className="text-cold-300 flex items-center gap-1 text-xs leading-none">
      {children}
    </div>
  </div>
);

const getTimeAgoString = (dateString: string): string =>
  formatDistanceToNow(new Date(dateString), { addSuffix: true });

export interface ProfileDetailsGameplayPlayerInfoProps extends HTMLAttributes<HTMLDivElement> {
  region: ValorantRegion;
  servers?: ValorantServer[];
  rankedQueueRate: number;
  matchesPlayed: number;
  lastPlayedAt?: string;
}

export const ProfileDetailsGameplayPlayerInfo = memo(
  function ProfileDetailsGameplayPlayerInfo(
    props: ProfileDetailsGameplayPlayerInfoProps,
  ) {
    const {
      region,
      servers,
      rankedQueueRate,
      matchesPlayed,
      lastPlayedAt,
      className,
      ...rest
    } = props;

    const isCompetitive = rankedQueueRate >= 0.5;
    const playstylePercentage = Math.round(
      (isCompetitive ? rankedQueueRate : 1 - rankedQueueRate) * 100,
    );
    const playstyleLabel = isCompetitive ? 'competitive' : 'casual';
    const timeAgo = lastPlayedAt ? getTimeAgoString(lastPlayedAt) : null;

    return (
      <Card variant="data" className={className} {...rest}>
        <p className="text-cold-300 text-sm leading-5 font-bold">Player Info</p>
        <div className="flex flex-col gap-3">
          {/* Region */}
          <InfoRow icon={<Valorant className="size-5" />}>
            <span className="font-bold">{getRegionDisplayName(region)}</span>
          </InfoRow>

          {/* Servers */}
          {servers && servers.length > 0 && (
            <InfoRow icon={<SignalHigh className="size-5" />}>
              <span className="font-normal">
                {servers.map(s => SERVER_DISPLAY_NAMES[s]).join(', ')}
              </span>
            </InfoRow>
          )}

          {/* Playstyle */}
          {matchesPlayed > 0 && (
            <InfoRow icon={<CrossedSwords className="size-5" />}>
              <span className="font-medium">{playstylePercentage}%</span>
              <span className="font-normal">{playstyleLabel}</span>
            </InfoRow>
          )}

          {/* Last Played */}
          {timeAgo && (
            <InfoRow icon={<Clock className="size-5" />}>
              <span className="font-normal">Last played {timeAgo}</span>
            </InfoRow>
          )}
        </div>
      </Card>
    );
  },
);
